import React, { useEffect } from 'react';
import { RadioCards, Flex, Text, TextArea, Button, Spinner, Tooltip } from '@radix-ui/themes';
import Icon from '@mdi/react';
import { mdiCreation, mdiDice3 } from '@mdi/js';
import api from '../../services/api';
import { useStore } from '../../store/store';
import avatar from '../../assets/avatar2.png';

export const LogLineGenerator = ({ setLogline }) => {
    const [mode, setMode] = React.useState('ai');
    const [aiLoglineInstructions, setAiLoglineInstructions] = React.useState('Use AI to generate a logline');
    const [loglines, setLoglines] = React.useState([]);
    const [showLoglineNumber, setShowLoglineNumber] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(false);
    const [state] = useStore();

    const loglineSuggestions = [
        "Steampunk adventure in a world of floating islands",
        "Mystery where dreams hold hidden truths",
        "Fantasy story about a blacksmith shaping history",
        "Survival thriller in an extreme environment",
        "Political drama in a society ruled by magic",
        "Horror tale involving a sinister reflection",
        "Sci-fi thriller about a hacker discovering a hidden truth",
        "Romance between two beings from different worlds",
        "Cosmic horror set in an isolated location",
        "Cyberpunk rebellion against an oppressive system",
        "Adventure where music holds magical power",
        "Detective mystery in a world with unusual rules",
        "Historical fantasy where secret forces shape the past",
        "Space opera following a crew on a high-stakes mission",
        "Comedy about an AI seeking an unexpected career",
        "Dark fairy tale with unpredictable consequences",
        "Horror-comedy featuring a reluctant supernatural entity",
        "Alien invasion told from the perspective of the invaders",
        "Arenas where cybernetic warriors battle for freedom",
        "Mystery where everyone wakes up with changed memories",
        "Time-travel romance with unexpected challenges",
        "Fantasy epic where mortals are drawn into a divine conflict",
        "War story told through an unconventional perspective",
        "Western with supernatural elements",
        "Mystery where art reveals hidden dangers",
        "Sci-fi road trip across a transformed world",
        "Comedy where aliens misunderstand human culture",
        "Family drama spanning multiple generations",
        "Thriller about a therapist who can see inside minds",
        "Martial arts fantasy where movement channels power",
        "Treasure hunt for an artifact that changes everything",
        "Love story between a human and an advanced AI",
        "Post-apocalyptic tale where lost knowledge holds power",
        "Spy thriller set in a seemingly perfect world",
        "Modern mythological story featuring ancient deities",
        "Dark fantasy about an artifact with a mind of its own",
        "Comedy about a hero struggling with retirement",
        "Horror where people vanish into a mysterious book",
        "Fantasy where celestial forces manipulate reality",
        "Sci-fi adventure following a rogue AI on a mission",
        "Noir detective story with hidden identities",
        "Romance between rivals with secret lives",
        "Urban fantasy where supernatural creatures rule the underworld",
        "Legal drama dealing with otherworldly cases",
        "Survival story about an explorer trapped in the unknown",
        "Haunted house horror with shifting architecture",
        "Magical story where words or music alter reality",
        "Love story involving reincarnation and forgotten memories",
        "Fantasy where weapons have personalities and wills of their own",
        "Thriller uncovering a secret hidden in ancient myths"
    ];

    const getRandomSuggestion = () => {
        const randomIndex = Math.floor(Math.random() * loglineSuggestions.length);
        return loglineSuggestions[randomIndex];
    };

    useEffect(() => {
        if (loglines.length > 0) {
            setLogline(loglines[showLoglineNumber]);
        }
    }, [loglines, showLoglineNumber, setLogline]);

    const generateLogline = async () => {
        setIsLoading(true);
        try {
            const result = await api.generateLoglines({loglineTheme: aiLoglineInstructions}, 1);
            setLoglines([...loglines, result.loglines[0]]);
            setShowLoglineNumber(loglines.length); // Show the newly generated logline
        } catch (error) {
            console.error("Error generating logline:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const handlePrevious = () => {
        setShowLoglineNumber((prev) => (prev > 0 ? prev - 1 : loglines.length - 1));
    };

    const handleNext = () => {
        setShowLoglineNumber((prev) => (prev < loglines.length - 1 ? prev + 1 : 0));
    };

    return (
        <>
            {state.novels.length === 0 ?
                <Flex className="tutorial-container" style={{}}>
                    <Flex className={"tutorial-text"}>
                        <img src={avatar} alt="Avatar" className="tutorial-avatar" />
                        <div>
                            Let's start!<br />
                            When creating a fiction novel, you can use the AI to generate a logline - or you can enter one manually.<br />
                            If using AI, enter what kind of story you would like and hit 'Generate Logline' (or click the die for inspiration).<br />
                            You can keep generating loglines until you find one you're happy with - don't worry, you can always go back and pick a previously generated one!<br />
                            </div>
                    </Flex>
                </Flex> : null}

                        <RadioCards.Root defaultValue="ai" columns={{ initial: "1", sm: "2" }}>
                    <RadioCards.Item value="ai" onClick={() => {setMode('ai')}}>
                        <Flex direction="column" width="100%">
                            <Text weight="bold">AI Generated</Text>
                            <Text>Use AI to generate a logline</Text>
                        </Flex>
                    </RadioCards.Item>
                    <RadioCards.Item value="manual" onClick={() => {setMode('manual')}}>
                        <Flex direction="column" width="100%">
                            <Text weight="bold">Manual</Text>
                            <Text>Manually type in your logline</Text>
                        </Flex>
                    </RadioCards.Item>
                </RadioCards.Root>
                <br />
            {loglines.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '20px 0' }}>
                    <Button 
                        variant="ghost" 
                        onClick={handlePrevious} 
                        disabled={loglines.length <= 1}
                        style={{ borderRadius: '50%', width: '40px', height: '40px', padding: 0 }}
                    >
                        ←
                    </Button>
                    
                    <div style={{ flex: 1, textAlign: 'center', padding: '0 20px' }}>
                        <Text>{loglines[showLoglineNumber]}</Text>
                        {loglines.length > 1 && (
                            <Text as="div" size="1" style={{ marginTop: '8px', opacity: 0.7 }}>
                                {showLoglineNumber + 1} of {loglines.length}
                            </Text>
                        )}
                    </div>
                    
                    <Button 
                        variant="ghost" 
                        onClick={handleNext}
                        disabled={loglines.length <= 1}
                        style={{ borderRadius: '50%', width: '40px', height: '40px', padding: 0 }}
                    >
                        →
                    </Button>
                </div>
            )}
            <div style={{margin: '0 auto', width: '100%', marginTop: '-30px'}}>
                <br />
                {mode === 'ai' && <div>
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr auto', 
                        gap: '8px', 
                        alignItems: 'start' 
                    }}>
                        <TextArea 
                            variant="soft" 
                            onChange={(e) => {setAiLoglineInstructions(e.target.value)}} 
                            value={aiLoglineInstructions}
                            placeholder="What kind of story do you want? E.g. 'Dark Fantasy epic set in an Ocean World'" 
                        />
                        <Tooltip content="Get a random logline prompt">
                            <Button 
                                variant="ghost"
                                style={{ 
                                    padding: '5px',
                                    borderRadius: '50%',
                                    width: '36px',
                                    height: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginLeft: '5px',
                                    marginRight: '5px',
                                    marginTop: '10px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setAiLoglineInstructions(getRandomSuggestion())}
                            >
                                <Icon path={mdiDice3} size={1.2} />
                            </Button>
                        </Tooltip>
                    </div>
                    <br />
                    <Button 
                        onClick={() => {generateLogline()}}
                        disabled={isLoading}
                        style={{background: '#6221ab', cursor: 'pointer'}}
                    >
                        {isLoading ? (
                            <Flex gap="2" align="center">
                                <Spinner size="small" />
                                <span>Generating...</span>
                            </Flex>
                        ) : (
                            <>
                            <Icon style={{transform: 'scale(0.8)'}} path={mdiCreation} size={1} />
                            {`Generate ${loglines.length > 1 ? 'another' : ''} Logline`}
                            </>
                        )}
                    </Button>
                </div>}

                {mode === 'manual' && <div>
                    <TextArea variant="soft" onChange={(e) => {setLogline(e.target.value)}} placeholder="Enter your logline here." />
                </div>}
                <br />
            </div>
        </>
    );
};

export default LogLineGenerator;